<template>
  <main class="q-home" style="margin-top: 5vh; ">
    <!-- ambient background -->
    <div class="q-bg" aria-hidden="true">
      <span class="q-orb q-orb-1"></span>
      <span class="q-orb q-orb-2"></span>
      <span class="q-orb q-orb-3"></span>
      <span class="q-grid"></span>
    </div>

    <div class="container q-hero">
      <div class="row align-items-center g-5">
        <!-- LEFT -->
        <div class="col-12 col-lg-6 q-left">
          <span class="q-pill q-fade-up" style="animation-delay: 0.05s;">
            <i class="bi bi-stars me-2"></i>AI study assistant
          </span>

          <h1 class="q-title mt-3 q-fade-up" style="animation-delay: 0.12s;">
            Turn your notes into
            <span class="q-grad">quizzes</span>,
            <span class="q-grad">flashcards</span>
            & a study plan.
          </h1>

          <p class="q-sub mt-3 q-fade-up" style="animation-delay: 0.18s;">
            Upload a photo of your notes. Quick Quiz AI creates interactive quizzes, summaries,
            and a personalized plan so you can study faster and remember more.
          </p>

          <div class="d-flex flex-wrap gap-3 mt-4 q-fade-up" style="animation-delay: 0.25s;">
            <a class="btn q-cta" href="/notes">
              <i class="bi bi-camera me-2"></i>Analyze Notes
            </a>
            

          <button class="btn q-secondary" type="button" @click="openHow">
            <i class="bi bi-play-circle me-2"></i>See how it works
          </button>
            <howItWorksModel v-model="howOpen" />
          </div>

          <div class="q-trust mt-4 q-fade-up" style="animation-delay: 0.32s;">
            <div class="q-trust-item">
              <i class="bi bi-shield-check me-2"></i>Private by default
            </div>
            <div class="q-trust-item">
              <i class="bi bi-lightning-charge me-2"></i>Instant results
            </div>
            <div class="q-trust-item">
              <i class="bi bi-check2-circle me-2"></i>Made for students
            </div>
          </div>
        </div>

        <!-- RIGHT -->
        <div class="col-12 col-lg-6 q-right">
          <div class="q-panel q-float-in" style="animation-delay: 0.14s;">
            <div class="q-panel-top d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center gap-3">
                <span class="q-mini-logo" aria-hidden="true"></span>
                <div>
                  <div class="q-panel-title">Analyze Notes</div>
                  <div class="q-panel-sub">Upload a photo or PDF to generate study material.</div>
                </div>
              </div>
              <span class="q-badge">Beta</span>
            </div>

            <div class="q-drop mt-3">
              <div class="q-drop-ic">
                <i class="bi bi-cloud-arrow-up"></i>
              </div>

              <div class="mt-3">
                <div class="q-drop-title">Drop your notes here</div>
                <div class="q-drop-sub">PNG, JPG, PDF • up to 10MB</div>
              </div>

              <div class="d-flex flex-wrap gap-3 mt-4 justify-content-center">
                <button class="btn q-upload" type="button">
                  <i class="bi bi-upload"></i><span>Upload file</span>
                </button>
                <button class="btn q-upload-soft" type="button">
                  <i class="bi bi-camera"></i><span>Use camera</span>
                </button>
              </div>

              <!-- tiny activity strip -->
              <div class="q-activity mt-4">
                <span class="q-dot"></span>
                <span class="q-activity-text">Ready to analyze</span>
                <span class="q-activity-bar" aria-hidden="true"></span>
              </div>
            </div>

            <div class="row g-3 mt-3">
              <div class="col-12 col-md-4">
                <div class="q-mini-card q-hoverlift">
                  <div class="q-mini-ic"><i class="bi bi-question-circle"></i></div>
                  <div class="q-mini-t">Quizzes</div>
                  <div class="q-mini-s">Auto-generated questions</div>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="q-mini-card q-hoverlift">
                  <div class="q-mini-ic"><i class="bi bi-stack"></i></div>
                  <div class="q-mini-t">Flashcards</div>
                  <div class="q-mini-s">Key terms & definitions</div>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="q-mini-card q-hoverlift">
                  <div class="q-mini-ic"><i class="bi bi-calendar2-check"></i></div>
                  <div class="q-mini-t">Study Plan</div>
                  <div class="q-mini-s">Personalized schedule</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FEATURES -->
      <section id="how" class="q-features mt-5">
        <div class="q-feat-head q-fade-up" style="animation-delay: 0.10s;">
          <h2 class="q-feat-title">
            Everything you need to <span class="q-grad">study smarter</span>
          </h2>
          <p class="q-feat-sub">
            Built to turn messy notes into clean material you can actually revise.
          </p>
        </div>

        <div class="row g-4 mt-1">
          <div class="col-12 col-lg-4">
            <div class="q-feature-card q-hoverlift q-fade-up" style="animation-delay: 0.14s;">
              <div class="q-feature-ic"><i class="bi bi-magic"></i></div>
              <h3 class="q-feature-title">Smart breakdowns</h3>
              <p class="q-feature-sub">
                Explanations in plain language with examples and step-by-step help.
              </p>
            </div>
          </div>

          <div class="col-12 col-lg-4">
            <div class="q-feature-card q-hoverlift q-fade-up" style="animation-delay: 0.18s;">
              <div class="q-feature-ic"><i class="bi bi-graph-up"></i></div>
              <h3 class="q-feature-title">Track progress</h3>
              <p class="q-feature-sub">
                Know what you understand, what you missed, and what to revise next.
              </p>
            </div>
          </div>

          <div class="col-12 col-lg-4">
            <div class="q-feature-card q-hoverlift q-fade-up" style="animation-delay: 0.22s;">
              <div class="q-feature-ic"><i class="bi bi-rocket-takeoff"></i></div>
              <h3 class="q-feature-title">Study faster</h3>
              <p class="q-feature-sub">
                Turn notes into quizzes + flashcards in seconds — not hours.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import howItWorksModel from './howItWorksModel.vue';
export default {
  name: "Home",
  components: { howItWorksModel },
  data() {
    return { howOpen: false };
  },
  methods: {
    openHow() { this.howOpen = true; },
    closeHow() { this.howOpen = false; },
  },
};
</script>


<style scoped>
/* --- background --- */
.q-home{
  position: relative;
  overflow-x: hidden;        /* IMPORTANT: kills the side “purple wall” */
  min-height: 100vh;
}

/* Make background fixed to viewport so it doesn't “end” mid-scroll */
.q-bg{
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: -1;
  overflow: hidden;
}

/* Smaller, safer orbs */
.q-orb{
  position: absolute;
  width: 30rem;
  height: 30rem;
  border-radius: 999px;
  filter: blur(4rem);
  opacity: 0.18;
  transform: translate3d(0,0,0);
  animation: q-drift 10s ease-in-out infinite alternate;
}

.q-orb-1{ top: -20%; left: -15%; background: rgba(45,108,255,1); }
.q-orb-2{ top: 10%; right: -18%; background: rgba(124,58,237,1); animation-duration: 12s; }
.q-orb-3{ bottom: -25%; left: 20%; background: rgba(255,61,141,1); animation-duration: 14s; }

/* Remove the grid mask stuff (that can cause seams in some setups) */
.q-grid{
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 30% 25%, rgba(255,255,255,0.05), transparent 45%),
    radial-gradient(circle at 70% 55%, rgba(255,255,255,0.04), transparent 55%);
  opacity: 0.35;
}

/* Optional: hard kill any accidental overflow caused by other elements */
:global(html), :global(body){
  overflow-x: hidden;
}

@keyframes q-drift{
  0% { transform: translate3d(0,0,0) scale(1); }
  100% { transform: translate3d(1.5rem,-1rem,0) scale(1.05); }
}


/* Container */
.q-hero { max-width: 72rem; position: relative; }

/* Pill */
.q-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 62.5rem;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  font-weight: 800;
  font-size: 0.95rem;
}

/* Title */
.q-title {
  font-size: clamp(2.2rem, 4vw, 3.4rem);
  font-weight: 950;
  letter-spacing: -0.06rem;
  line-height: 1.05;
  color: rgba(255, 255, 255, 0.96);
}

.q-grad {
  background: linear-gradient(90deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* Subtitle */
.q-sub {
  font-size: 1.05rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.75);
  max-width: 92%;
}

/* Buttons */
.q-cta {
  border-radius: 62.5rem;
  padding: 0.78rem 1.25rem;
  font-weight: 950;
  color: #fff;
  border: 0;
  background: linear-gradient(90deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  box-shadow: 0 1.125rem 2.25rem rgba(0, 0, 0, 0.35);
  transition: transform 160ms ease, filter 160ms ease;
}
.q-cta:hover { filter: brightness(1.06); transform: translateY(-0.08rem); }

.q-secondary {
  border-radius: 62.5rem;
  padding: 0.78rem 1.25rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.92);
  border: 0.0625rem solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
  transition: background 160ms ease, transform 160ms ease;
}
.q-secondary:hover { background: rgba(255, 255, 255, 0.08); transform: translateY(-0.06rem); }

/* Trust */
.q-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.72);
  font-weight: 850;
}

.q-trust-item {
  display: inline-flex;
  align-items: center;
  padding: 0.6rem 0.9rem;
  border-radius: 1.1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  transition: transform 160ms ease, background 160ms ease;
}
.q-trust-item:hover { transform: translateY(-0.08rem); background: rgba(255,255,255,0.06); }

/* Panel */
.q-panel {
  border-radius: 1.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
  box-shadow: 0 1.75rem 4rem rgba(0, 0, 0, 0.35);
  padding: 1.25rem;
  backdrop-filter: blur(14px);
}

.q-mini-logo {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 1rem;
  background: linear-gradient(145deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  opacity: 0.95;
  box-shadow: 0 1.25rem 2.5rem rgba(0,0,0,0.25);
}

.q-panel-title { font-weight: 950; color: rgba(255, 255, 255, 0.95); }
.q-panel-sub { color: rgba(255, 255, 255, 0.65); font-weight: 700; font-size: 0.95rem; }

/* Badge */
.q-badge {
  padding: 0.35rem 0.7rem;
  border-radius: 62.5rem;
  font-weight: 950;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.12);
}

/* Drop */
.q-drop {
  border-radius: 1.35rem;
  padding: 1.6rem 1.25rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.20);
  border: 0.0625rem dashed rgba(255, 255, 255, 0.20);
  transition: border-color 200ms ease, background 200ms ease, transform 200ms ease;
}
.q-drop:hover {
  border-color: rgba(255,255,255,0.32);
  background: rgba(255,255,255,0.04);
  transform: translateY(-0.08rem);
}

.q-drop-ic {
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.06);
  border: 0.0625rem solid rgba(255,255,255,0.10);
  color: rgba(255,255,255,0.9);
  font-size: 1.35rem;
}

.q-drop-title { font-weight: 950; color: rgba(255, 255, 255, 0.92); }
.q-drop-sub { color: rgba(255, 255, 255, 0.65); font-weight: 700; font-size: 0.95rem; }

/* Upload buttons */
.q-upload,
.q-upload-soft {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.q-upload {
  border-radius: 62.5rem;
  padding: 0.72rem 1.15rem;
  font-weight: 950;
  border: 0.0625rem solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.92);
  transition: transform 160ms ease, background 160ms ease;
}
.q-upload:hover { background: rgba(255,255,255,0.10); transform: translateY(-0.06rem); }

.q-upload-soft {
  border-radius: 62.5rem;
  padding: 0.72rem 1.15rem;
  font-weight: 950;
  border: 0;
  color: #fff;
  background: linear-gradient(90deg, rgba(45,108,255,0.65), rgba(124,58,237,0.65), rgba(255,61,141,0.65));
  transition: transform 160ms ease, filter 160ms ease;
}
.q-upload-soft:hover { filter: brightness(1.07); transform: translateY(-0.06rem); }

/* Activity strip */
.q-activity {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 0.9rem;
  border-radius: 1.1rem;
  background: rgba(255,255,255,0.04);
  border: 0.0625rem solid rgba(255,255,255,0.08);
  justify-content: center;
}

.q-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 999px;
  background: #ff3d8d;
  box-shadow: 0 0 0 0.35rem rgba(255,61,141,0.18);
  animation: q-pulse 1.8s ease-in-out infinite;
}

.q-activity-text {
  font-weight: 900;
  color: rgba(255,255,255,0.78);
}

.q-activity-bar {
  width: 28%;
  height: 0.4rem;
  border-radius: 999px;
  background: rgba(255,255,255,0.10);
  position: relative;
  overflow: hidden;
}
.q-activity-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  width: 45%;
  background: linear-gradient(90deg, rgba(45,108,255,0.0), rgba(45,108,255,0.7), rgba(255,61,141,0.0));
  transform: translateX(-120%);
  animation: q-scan 1.8s ease-in-out infinite;
}

/* Mini cards */
.q-mini-card {
  height: 100%;
  border-radius: 1.35rem;
  padding: 1.05rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.q-mini-ic {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
  color: rgba(255, 255, 255, 0.88);
  margin-bottom: 0.75rem;
}

.q-mini-t { font-weight: 950; color: rgba(255, 255, 255, 0.92); }
.q-mini-s { color: rgba(255, 255, 255, 0.65); font-weight: 700; font-size: 0.95rem; }

/* Features */
.q-feat-head { max-width: 46rem; }
.q-feat-title {
  font-size: clamp(1.55rem, 2.4vw, 2rem);
  font-weight: 950;
  color: rgba(255,255,255,0.95);
  letter-spacing: -0.03rem;
  margin: 0;
}
.q-feat-sub {
  color: rgba(255,255,255,0.70);
  font-weight: 750;
  line-height: 1.6;
  margin-top: 0.75rem;
  margin-bottom: 0;
}

.q-feature-card {
  border-radius: 1.6rem;
  padding: 1.6rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  height: 100%;
}

.q-feature-ic {
  width: 3rem;
  height: 3rem;
  border-radius: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
}

.q-feature-title {
  font-weight: 950;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.2rem;
}
.q-feature-sub {
  color: rgba(255, 255, 255, 0.70);
  line-height: 1.65;
  margin-bottom: 0;
  font-weight: 700;
}

/* Hover lift utility */
.q-hoverlift {
  transition: transform 180ms ease, background 180ms ease, border-color 180ms ease;
}
.q-hoverlift:hover {
  transform: translateY(-0.12rem);
  background: rgba(255,255,255,0.055);
  border-color: rgba(255,255,255,0.12);
}

/* Animations (no JS needed) */
.q-fade-up {
  opacity: 0;
  transform: translateY(0.7rem);
  animation: q-fade-up 700ms cubic-bezier(.2,.8,.2,1) forwards;
}

.q-float-in {
  opacity: 0;
  transform: translateY(0.9rem) scale(0.985);
  animation: q-float-in 850ms cubic-bezier(.2,.8,.2,1) forwards;
}

@keyframes q-fade-up {
  to { opacity: 1; transform: translateY(0); }
}

@keyframes q-float-in {
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes q-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

@keyframes q-scan {
  0% { transform: translateX(-120%); }
  60% { transform: translateX(180%); }
  100% { transform: translateX(180%); }
}

@keyframes q-drift {
  0% { transform: translate3d(0,0,0) scale(1); }
  100% { transform: translate3d(2rem, -1.5rem, 0) scale(1.05); }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .q-fade-up, .q-float-in, .q-orb, .q-dot, .q-activity-bar::after { animation: none !important; }
  .q-hoverlift, .q-cta, .q-secondary, .q-upload, .q-upload-soft, .q-trust-item { transition: none !important; }
}

/* Mobile */
@media (max-width: 62rem) {
  .q-sub { max-width: 100%; }
  .q-activity-bar { width: 40%; }
  .q-feat-head { max-width: 100%; }
}




</style>
